import { Analytics, setAnalyticsCollectionEnabled } from 'firebase/analytics'
import { initializeAnalytics } from '../config/firebase'
import type { UserData, GeolocationData, DeviceInfo, BrowserInfo } from '../types'

/**
 * AnalyticsManager handles Firebase Analytics integration
 * Manages event tracking, user data collection, and consent management
 * Requirements: 1.4, 2.1, 2.2, 4.1, 6.4, 6.5
 */
export class AnalyticsManager {
  private analytics: Analytics | null = null
  private consentGiven: boolean = false
  private initialized: boolean = false
  private initializationError?: string

  /**
   * Initialize Firebase Analytics with consent management and comprehensive error handling
   * Must be called before any analytics operations
   */
  async initialize(consentGiven: boolean = false): Promise<void> {
    try {
      this.consentGiven = consentGiven
      
      // Check if we're in a supported environment
      if (typeof window === 'undefined') {
        throw new Error('Analytics not supported in server-side environment')
      }
      
      // Check for required APIs
      if (typeof fetch === 'undefined') {
        throw new Error('Fetch API not supported - required for Firebase Analytics')
      }
      
      // Initialize Firebase Analytics with error handling
      try {
        this.analytics = await initializeAnalytics(consentGiven)
      } catch (firebaseError) {
        console.error('Firebase Analytics initialization failed:', firebaseError)
        
        // Check for specific Firebase errors
        if (firebaseError instanceof Error) {
          if (firebaseError.message.includes('not supported')) {
            console.warn('Firebase Analytics not supported in this environment - continuing without analytics')
            this.initialized = false
            return
          } else if (firebaseError.message.includes('network')) {
            throw new Error('Network error during Firebase initialization - check internet connection')
          } else if (firebaseError.message.includes('configuration')) {
            throw new Error('Firebase configuration error - check project settings')
          }
        }
        
        throw firebaseError
      }
      
      if (this.analytics) {
        // Set initial consent state with error handling
        try {
          await this.setConsent(consentGiven)
        } catch (consentError) {
          console.error('Failed to set initial consent state:', consentError)
          // Continue initialization even if consent setting fails
        }
        
        this.initialized = true
        console.log('AnalyticsManager initialized successfully')
        
        // Test connection if consent is given
        if (consentGiven) {
          try {
            await this.validateAnalyticsConnection()
          } catch (validationError) {
            console.warn('Analytics connection validation failed:', validationError)
            // Don't fail initialization for validation errors
          }
        }
      } else {
        console.warn('Analytics not available - running in fallback mode')
        this.initialized = false
      }
    } catch (error) {
      console.error('Failed to initialize AnalyticsManager:', error)
      this.initialized = false
      
      // Store initialization error for debugging
      this.initializationError = error instanceof Error ? error.message : 'Unknown initialization error'
    }
  }

  /**
   * Set user consent for analytics collection
   * Updates Firebase Analytics consent mode
   */
  async setConsent(granted: boolean): Promise<void> {
    this.consentGiven = granted
    
    if (this.analytics) {
      try {
        // Use gtag to set consent mode (proper way for Firebase Analytics)
        if (typeof (window as any).gtag !== 'undefined') {
          (window as any).gtag('consent', 'update', {
            analytics_storage: granted ? 'granted' : 'denied',
            ad_storage: 'denied', // We don't use ads
            functionality_storage: granted ? 'granted' : 'denied',
            personalization_storage: 'denied', // We don't personalize
            security_storage: 'granted' // Always allow security storage
          })
        }

        // Enable/disable analytics collection
        setAnalyticsCollectionEnabled(this.analytics, granted)
        
        console.log(`🔒 Analytics consent ${granted ? 'granted' : 'denied'}`)
        
        // If consent is granted, fire a test event to verify connection
        if (granted) {
          console.log('🧪 Testing analytics connection...')
        }
      } catch (error) {
        console.error('❌ Failed to set analytics consent:', error)
      }
    }
  }

  /**
   * Fire an analytics event with optional user data and comprehensive error handling
   * Handles all required analytics events from requirements
   */
  async fireEvent(eventName: string, customData?: Record<string, any>): Promise<void> {
    // Early return if analytics not available
    if (!this.initialized || !this.analytics || !this.consentGiven) {
      console.log(`Analytics event skipped (${eventName}) - consent not given or analytics not available`)
      return
    }

    try {
      // Validate event name
      if (!eventName || typeof eventName !== 'string') {
        throw new Error('Invalid event name provided')
      }

      // Collect user data for the event with error handling
      let userData: UserData
      try {
        userData = await this.collectUserData()
      } catch (userDataError) {
        console.warn('Failed to collect user data for analytics event:', userDataError)
        // Continue with empty user data
        userData = {
          deviceInfo: this.collectDeviceInfo(),
          browserInfo: this.collectBrowserInfo()
        }
      }
      
      // Prepare event data with validation
      const eventData: Record<string, any> = {
        timestamp: Date.now(),
        ...customData
      }

      // Add user data if available
      try {
        if (userData.deviceInfo) {
          eventData.device_platform = userData.deviceInfo.platform
          eventData.screen_width = userData.deviceInfo.screenWidth
          eventData.screen_height = userData.deviceInfo.screenHeight
          eventData.pixel_ratio = userData.deviceInfo.pixelRatio
          eventData.touch_support = userData.deviceInfo.touchSupport
        }

        if (userData.browserInfo) {
          eventData.browser_name = userData.browserInfo.name
          eventData.browser_version = userData.browserInfo.version
          eventData.browser_language = userData.browserInfo.language
          eventData.webgl_support = userData.browserInfo.webglSupport
          eventData.webrtc_support = userData.browserInfo.webrtcSupport
        }

        if (userData.location) {
          eventData.latitude = userData.location.latitude
          eventData.longitude = userData.location.longitude
          eventData.location_accuracy = userData.location.accuracy
        }
      } catch (dataError) {
        console.warn('Error adding user data to analytics event:', dataError)
        // Continue without user data
      }

      // Validate event data size (GA has limits)
      const eventDataString = JSON.stringify(eventData)
      if (eventDataString.length > 8192) { // 8KB limit
        console.warn('Analytics event data too large, truncating...')
        // Keep only essential data
        const essentialData = {
          timestamp: eventData.timestamp,
          browser_name: eventData.browser_name,
          device_platform: eventData.device_platform
        }
        Object.assign(eventData, essentialData)
      }

      // Send event via gtag with error handling
      if (typeof (window as any).gtag !== 'undefined') {
        try {
          (window as any).gtag('event', eventName, eventData)
          
          console.log(`🔥 Analytics event fired: ${eventName}`, eventData)
          console.log(`📊 Sent to Google Analytics (${this.analytics?.app.options.measurementId})`)
          
          // Debug info for development
          const isDevelopment = window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1' ||
                               window.location.hostname.includes('localhost')
          
          if (isDevelopment) {
            console.log(`🔍 Debug: Event sent via gtag - check GA Debug View`)
          }
        } catch (gtagError) {
          console.error('❌ gtag event firing failed:', gtagError)
          throw new Error(`Failed to send analytics event via gtag: ${gtagError}`)
        }
      } else {
        console.error('❌ gtag not available - analytics event not sent')
        throw new Error('gtag not available for analytics event')
      }
    } catch (error) {
      console.error(`Failed to fire analytics event ${eventName}:`, error)
      
      // Don't throw error to prevent breaking the application flow
      // Analytics failures should not stop the AR experience
    }
  }

  /**
   * Fire consent given event
   * Requirement: 1.4 - Analytics consent tracking
   */
  async fireConsentGiven(): Promise<void> {
    await this.fireEvent('analytics_consent_given', {
      consent_method: 'checkbox',
      consent_timestamp: Date.now()
    })
  }

  /**
   * Fire permission events
   * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5 - Permission tracking
   */
  async firePermissionsGranted(): Promise<void> {
    await this.fireEvent('permissions_granted', {
      permissions: ['camera', 'location'],
      permission_method: 'browser_api'
    })
  }

  async fireCameraPermissionDenied(): Promise<void> {
    await this.fireEvent('permission_denied', {
      permission_type: 'camera',
      permission_method: 'browser_api'
    })
  }

  async fireLocationPermissionDenied(): Promise<void> {
    await this.fireEvent('permission_denied', {
      permission_type: 'location',
      permission_method: 'browser_api'
    })
  }

  async firePermissionsDenied(): Promise<void> {
    await this.fireEvent('permissions_denied', {
      required_permissions: ['camera', 'location'],
      permission_method: 'browser_api'
    })
  }

  /**
   * Fire ROQ scan success event with location data
   * Requirement: 4.1 - Marker detection tracking
   */
  async fireRoqScanSuccess(): Promise<void> {
    await this.fireEvent('roq_scan_success', {
      marker_type: 'roq_target',
      scan_method: 'ar_js_marker'
    })
  }

  /**
   * Fire CTA click events
   * Requirements: 6.4, 6.5 - CTA interaction tracking
   */
  async fireCTAClick2D(): Promise<void> {
    await this.fireEvent('cta_click_2D', {
      cta_type: '2d_overlay',
      cta_text: 'Visit ROQ.us'
    })
  }

  async fireCTAClick3D(): Promise<void> {
    await this.fireEvent('cta_click_3D', {
      cta_type: '3d_object',
      cta_position: 'above_marker'
    })
  }

  /**
   * Collect comprehensive user data for analytics
   * Gathers device, browser, and location information
   */
  async collectUserData(): Promise<UserData> {
    const userData: UserData = {
      deviceInfo: this.collectDeviceInfo(),
      browserInfo: this.collectBrowserInfo()
    }

    // Collect location data if permission granted
    try {
      userData.location = await this.collectLocationData()
    } catch (error) {
      console.log('Location data not available:', error)
    }

    return userData
  }

  /**
   * Collect device information
   * Gathers screen, platform, and capability data
   */
  private collectDeviceInfo(): DeviceInfo {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      pixelRatio: window.devicePixelRatio || 1,
      touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0
    }
  }

  /**
   * Collect browser information
   * Determines browser type, version, and capabilities
   */
  private collectBrowserInfo(): BrowserInfo {
    const userAgent = navigator.userAgent
    let browserName = 'Unknown'
    let browserVersion = 'Unknown'

    // Detect browser type and version
    if (userAgent.includes('CriOS')) {
      browserName = 'Chrome'
      const match = userAgent.match(/CriOS\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Edg/')) {
      browserName = 'Edge'
      const match = userAgent.match(/Edg\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Chrome')) {
      browserName = 'Chrome'
      const match = userAgent.match(/Chrome\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox'
      const match = userAgent.match(/Firefox\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Safari')) {
      browserName = 'Safari'
      const match = userAgent.match(/Version\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Edge')) {
      browserName = 'Edge'
      const match = userAgent.match(/Edge\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    }

    return {
      name: browserName,
      version: browserVersion,
      language: navigator.language,
      webglSupport: this.checkWebGLSupport(),
      webrtcSupport: this.checkWebRTCSupport()
    }
  }

  /**
   * Collect location data only if permission was already granted
   * Does not request permission - relies on ApplicationController permission flow
   */
  private async collectLocationData(): Promise<GeolocationData | undefined> {
    // Don't collect location data automatically - wait for explicit permission grant
    // This prevents triggering location permission request during analytics initialization
    console.log('Location data collection skipped - waiting for explicit permission grant')
    return undefined
  }

  /**
   * Collect location data after permission has been explicitly granted
   * Called by ApplicationController after successful location permission
   */
  async collectLocationDataWithPermission(): Promise<GeolocationData | undefined> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.log('Geolocation not supported')
        resolve(undefined)
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('Location data collected successfully')
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          })
        },
        (error) => {
          console.error('Failed to collect location data:', error)
          resolve(undefined)
        },
        {
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
          enableHighAccuracy: false
        }
      )
    })
  }

  /**
   * Check if WebGL is supported
   * Required for AR functionality
   */
  private checkWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      return !!gl
    } catch (error) {
      return false
    }
  }

  /**
   * Check if WebRTC is supported
   * Required for camera access
   */
  private checkWebRTCSupport(): boolean {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  }

  /**
   * Get current initialization status
   */
  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Get current consent status
   */
  hasConsent(): boolean {
    return this.consentGiven
  }

  /**
   * Test analytics connection by firing a test event
   * Useful for debugging analytics setup
   */
  async testConnection(): Promise<void> {
    if (!this.initialized || !this.analytics) {
      console.error('❌ Analytics not initialized - cannot test connection')
      return
    }

    if (!this.consentGiven) {
      console.error('❌ Analytics consent not given - cannot test connection')
      return
    }

    console.log('🧪 Testing analytics connection...')
    
    try {
      await this.fireEvent('test_connection', {
        test_timestamp: Date.now(),
        test_source: 'analytics_manager'
      })
      
      console.log('✅ Test event sent successfully')
      console.log('🔍 Check Google Analytics Debug View in 10-30 seconds')
      console.log(`🔗 Debug View URL: https://analytics.google.com/analytics/web/#/p${this.analytics.app.options.projectId}/reports/realtime`)
    } catch (error) {
      console.error('❌ Failed to send test event:', error)
    }
  }



  /**
   * Validate analytics connection by testing basic functionality
   */
  private async validateAnalyticsConnection(): Promise<void> {
    if (!this.analytics || !this.consentGiven) {
      throw new Error('Analytics not initialized or consent not given')
    }
    
    // Check if gtag is available
    if (typeof (window as any).gtag === 'undefined') {
      throw new Error('gtag not available - analytics events cannot be sent')
    }
    
    // Test basic event firing (this won't actually send data in development)
    try {
      (window as any).gtag('event', 'analytics_validation', {
        validation_timestamp: Date.now(),
        validation_source: 'analytics_manager'
      })
      console.log('Analytics connection validation successful')
    } catch (error) {
      throw new Error(`Analytics validation failed: ${error}`)
    }
  }

  /**
   * Get initialization error if any
   */
  getInitializationError(): string | undefined {
    return this.initializationError
  }

  /**
   * Get analytics configuration info for debugging
   */
  getDebugInfo(): Record<string, any> {
    return {
      initialized: this.initialized,
      consentGiven: this.consentGiven,
      analyticsAvailable: !!this.analytics,
      gtagAvailable: typeof (window as any).gtag !== 'undefined',
      projectId: this.analytics?.app.options.projectId,
      measurementId: this.analytics?.app.options.measurementId,
      hostname: window.location.hostname,
      isDevelopment: window.location.hostname === 'localhost' || 
                    window.location.hostname === '127.0.0.1' ||
                    window.location.hostname.includes('localhost'),
      initializationError: this.initializationError
    }
  }
}